import type { ComponentProps, FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { usePageTransition } from '@/components/motion/transition-provider'

interface TransitionLinkProps extends Omit<ComponentProps<typeof Link>, 'href'> {
    href: string
}

/**
 * Internal link that plays the Cortex page transition before navigating.
 * Modified clicks (new tab, etc.) and external links behave normally.
 */
export const TransitionLink: FC<TransitionLinkProps> = ({
    href,
    onClick,
    children,
    ...rest
}): ReactNode => {
    const { navigate } = usePageTransition()

    const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
        onClick?.(event)

        if (event.defaultPrevented) return
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        if (rest.target && rest.target !== '_self') return
        if (!href.startsWith('/') && !href.startsWith('#')) return

        event.preventDefault()
        navigate(href.startsWith('#') ? `${window.location.pathname}${href}` : href)
    }

    return (
        <Link
            href={href}
            onClick={handleClick}
            {...rest}
        >
            {children}
        </Link>
    )
}
